<template>
  <model-form
    ref="modelForm"
    :model="boxOfficeReport"
    :title="`${boxOfficeReport.name} (${boxOfficeReport.id})`"
  >
    <div class="row q-col-gutter-md">
      <q-banner
        v-if="!studio"
        class="bg-warning col-12 row items-center q-mb-md"
      >
        <template #avatar>
          <q-icon
            name="warning"
            color="negative"
          />
        </template>
        <div class="row justify-between items-center">
          There is no studio associated with this movie, unable to send BORs
        </div>
      </q-banner>
      <div class="col-12 row q-col-gutter-x-sm items-center">
        <iso-date-input
          v-model="boxOfficeReport.startDate"
          class="col-6"
          label="Start Date"
          read-only
        />
        <iso-date-input
          v-model="boxOfficeReport.endDate"
          class="col-6"
          label="End Date"
          read-only
        />
      </div>
      <div class="col-12 row q-col-gutter-x-sm items-center">
        <model-form-toggle
          field="hasFlatFee"
          class="col-6 q-pb-md"
          label="Has Flat Fee"
        />
        <model-form-input
          v-if="boxOfficeReport.hasFlatFee"
          field="flatFee"
          class="col-6"
          label="Flat Fee"
          :prefix="currentCircuit.localeCurrencySymbol"
          type="number"
          value-type="Number"
          step="25"
        />
        <model-form-input
          v-else
          field="termPercentHundredths"
          class="col-6"
          label="Term"
          suffix=" %"
          type="number"
          value-type="Number"
          step="0.01"
          min="0"
          max="100"
          :decode="val => val / 100.0"
          :encode="val => Math.min(Math.max(val, 0), 100) * 100"
          :rules="[
            val => !$_.isNil(val)|| 'Field is required',
            val => val >= 0 || 'Value must be greater than or equal to 0',
            val => val <= (100 * 100) || 'Value must be less than or equal to 100'
          ]"
        />
      </div>
      <div class="col-12 row q-col-gutter-x-sm items-center">
        <model-form-toggle
          field="hasMinimumGuarantee"
          class="col-6 q-pb-md"
          label="Has Minimum Guarantee"
          :disable="boxOfficeReport.hasFlatFee"
        />
        <model-form-input
          field="minimumGuarantee"
          class="col-6"
          label="Minimum Guarantee"
          :prefix="currentCircuit.localeCurrencySymbol"
          type="number"
          value-type="Number"
          step="25"
          :disable="boxOfficeReport.hasFlatFee || !boxOfficeReport.hasMinimumGuarantee"
        />
      </div>
      <div class="col-12 row q-col-gutter-x-sm items-center">
        <model-form-toggle
          field="hasRetentionFee"
          class="col-6 q-pb-md"
          label="Has Retention Fee"
        >
          <q-tooltip>Retain a portion of the rental fee for associated payments.</q-tooltip>
        </model-form-toggle>
        <model-form-input
          field="retentionFeePercentHundredths"
          class="col-6"
          :decode="val => val / 100.0"
          :encode="val => Math.min(Math.max(val, 0), 100) * 100"
          label="Retention Fee"
          type="number"
          value-type="Number"
          suffix=" %"
          step="0.01"
          min="0"
          max="100"
          :disable="!boxOfficeReport.hasRetentionFee"
          :rules="[
            val => !$_.isNil(val) || 'Field is required',
            val => val >= 0 || 'Value must be greater than or equal to 0',
            val => val <= (100 * 100) || 'Value must be less than or equal to 100'
          ]"
        >
          <q-tooltip>Percentage of the rental fee retained for other payments.</q-tooltip>
        </model-form-input>
      </div>

      <div class="col-12 row q-col-gutter-x-sm items-center">
        <div class="col-6" />
        <model-form-input
          field="depositPaid"
          class="col-6"
          label="Deposit Paid"
          :prefix="currentCircuit.localeCurrencySymbol"
          type="number"
          value-type="Number"
          step="25"
          :rules="[val => !$_.isNil(val) || 'Field is required']"
        />
      </div>

      <div class="col-12 row q-col-gutter-x-sm items-center">
        <model-form-input
          field="invoiceNumber"
          class="col-3"
          label="Invoice Number"
          clearable
        />
        <iso-date-input
          field="invoiceDate"
          class="col-3"
          label="Invoice Date"
          clearable
        />
        <model-form-input
          field="invoiceAmount"
          class="col-4"
          label="Invoice Amount"
          :prefix="currentCircuit.localeCurrencySymbol"
          type="number"
          value-type="Number"
          step="25"
          :rules="[val => !$_.isNil(val) || 'Field is required']"
        />
        <model-form-checkbox
          field="invoicePaid"
          class="col-2 q-pb-md"
          label="Invoice Paid"
        />
      </div>
      <div
        v-if="boxOfficeReport.hasRetentionFee"
        class="col-12 row q-col-gutter-x-sm items-center"
      >
        <model-form-input
          field="retentionInvoiceNumber"
          class="col-3"
          label="Retention Invoice Number"
          clearable
        />
        <iso-date-input
          field="retentionInvoiceDate"
          class="col-3"
          label="Retention Invoice Date"
          clearable
        />
        <model-form-input
          field="retentionInvoiceAmount"
          class="col-4"
          label="Retention Invoice Amount"
          :prefix="currentCircuit.localeCurrencySymbol"
          type="number"
          value-type="Number"
          step="25"
          :rules="[val => !$_.isNil(val) || 'Field is required']"
        />
        <model-form-checkbox
          field="retentionInvoicePaid"
          class="col-2 q-pb-md"
          label="Retention Paid"
        />
      </div>

      <div class="col-12 row q-gutter-sm items-center">
        <div
          v-if="!boxOfficeReport.hasFlatFee"
          class="col column text-center"
        >
          <div class="col text-bold">
            Gross Sales
          </div>
          <div class="col">
            <i18n-n
              :value="boxOfficeReport.grossAmount"
              format="currency"
              scope="global"
            />
          </div>
        </div>
        <div
          v-if="!boxOfficeReport.hasFlatFee"
          class="col column text-center"
        >
          <div class="col text-bold">
            Percent
          </div>
          <div class="col">
            {{ boxOfficeReport.termPercentHundredths / 100 }}%
          </div>
        </div>
        <div class="col column text-center">
          <div class="col text-bold">
            Rental Fee
          </div>
          <div class="col">
            <i18n-n
              :value="boxOfficeReport.rentalFee"
              format="currency"
              scope="global"
            />
          </div>
        </div>
        <div
          v-if="boxOfficeReport.hasMinimumGuarantee"
          class="col column text-center"
        >
          <div class="col text-bold">
            Guarantee
          </div>
          <div class="col">
            <i18n-n
              :value="boxOfficeReport.minimumGuarantee"
              format="currency"
              scope="global"
            />
          </div>
        </div>
        <div
          v-if="boxOfficeReport.hasRetentionFee"
          class="col column text-center"
        >
          <div class="col text-bold">
            Retained
          </div>
          <div class="col">
            <i18n-n
              :value="boxOfficeReport.retentionDue"
              format="currency"
              scope="global"
            />
          </div>
        </div>
        <div class="col column text-center">
          <div class="col text-bold">
            Total Due
          </div>
          <div class="col">
            <i18n-n
              :value="boxOfficeReport.amountDue"
              format="currency"
              scope="global"
            />
          </div>
        </div>
        <div
          v-if="boxOfficeReport.depositPaid"
          class="col column text-center"
        >
          <div class="col text-bold">
            Deposit
          </div>
          <div class="col">
            <i18n-n
              :value="boxOfficeReport.depositPaid"
              format="currency"
              scope="global"
            />
          </div>
        </div>
        <div
          v-if="boxOfficeReport.invoiceAmount"
          class="col column text-center"
        >
          <div class="col text-bold">
            Invoice Amount
          </div>
          <div class="col">
            <i18n-n
              :value="boxOfficeReport.invoiceAmount"
              format="currency"
              scope="global"
            />
          </div>
        </div>
        <div class="col column text-center">
          <div class="col text-bold">
            Remaining Due
          </div>
          <div class="col">
            <i18n-n
              :value="boxOfficeReport.remainingDue"
              format="currency"
              scope="global"
            />
          </div>
        </div>
      </div>

      <div class="col-12 row q-mt-lg">
        <model-form-input
          field="notes"
          class="col"
          label="Notes (internal, not sent to studio)"
          type="textarea"
        />
      </div>

      <div class="col-12 row justify-evenly q-gutter-sm">
        <div>
          <q-btn
            label="Force Recompute"
            color="negative"
            outline
            @loading="forceRecomputeLoading"
            @click="forceRecompute"
          />
          <div
            v-if="boxOfficeReport.computedAt"
            class="q-mt-sm text-subtitle2 text-grey-7 text-center"
          >
            {{ boxOfficeReport.computedAt.i18n('short') }}
          </div>
        </div>
        <div>
          <q-btn
            label="Send Email BOR"
            color="secondary"
            outline
            :disable="!studio || !studio.reportByEmail || !studio.reportingEmail"
            @loading="sendEmailBorLoading"
            @click="sendOrConfirm(sendEmailBor)"
          />
          <q-tooltip v-if="!studio">
            There is not studio associated with this movie, unable to send BORs
          </q-tooltip>
          <q-tooltip v-else-if="!studio.reportByEmail || !studio.reportingEmail">
            This studio is not configured to send Email BORs
          </q-tooltip>
          <div
            v-if="boxOfficeReport.emailBorSentAt"
            class="q-mt-sm text-subtitle2 text-grey-7 text-center"
          >
            <q-icon
              name="checkmark"
              color="positive"
              size="sm"
              class="justify-start items-center"
            />
            {{ boxOfficeReport.emailBorSentAt.i18n('short') }}
          </div>
        </div>
        <div>
          <q-btn
            label="Send Email BOR Summary"
            color="warning"
            outline
            :disable="!studio || !studio.reportByEmail || !studio.useEmailSummary || !studio.reportingEmail"
            @loading="sendEmailBorSummaryLoading"
            @click="sendOrConfirm(sendEmailBorSummary)"
          />
          <q-tooltip v-if="!studio">
            There is not studio associated with this movie, unable to send BORs
          </q-tooltip>
          <q-tooltip v-else-if="!studio.reportByEmail || !studio.reportingEmail">
            This studio is not configured to send Email BORs
          </q-tooltip>
          <div
            v-if="boxOfficeReport.emailBorSentAt"
            class="q-mt-sm text-subtitle2 text-grey-7 text-center"
          >
            <q-icon
              name="checkmark"
              color="positive"
              size="sm"
              class="justify-start items-center"
            />
            {{ boxOfficeReport.emailBorSentAt.i18n('short') }}
          </div>
        </div>
        <div>
          <q-btn
            label="Submit SWIFT eBOR"
            color="primary"
            outline
            :disable="!studio?.reportBySwiftEbor || !boxOfficeReport.isDue"
            @click="sendOrConfirm(submitSwiftEbor)"
          />
          <q-tooltip v-if="!studio?.reportBySwiftEbor">
            This studio is not configured to send SWIFT eBORs
          </q-tooltip>
          <q-tooltip v-else-if="!boxOfficeReport.isDue">
            Swift cannot be submitted until after the end date is past
          </q-tooltip>
          <div
            v-if="boxOfficeReport.swiftEborSubmittedAt"
            class="q-mt-sm text-subtitle2 text-grey-7 text-center"
          >
            <q-icon
              name="checkmark"
              color="positive"
              size="sm"
              class="justify-start items-center"
            />
            {{ boxOfficeReport.swiftEborSubmittedAt.i18n('short') }}
          </div>
        </div>
        <div>
          <q-btn
            label="View Summary"
            outline
            :disable="boxOfficeReport.isDirty"
            @click="boxOfficeReport.viewSummary()"
          />
        </div>
        <div>
          <q-btn
            label="View"
            outline
            :disable="boxOfficeReport.isDirty"
            @click="boxOfficeReport.view()"
          />
        </div>
      </div>

      <q-tabs
        v-model="tab"
        class="col-12 text-grey q-mt-sm"
        active-color="primary"
        indicator-color="primary"
        inline-label
        align="justify"
      >
        <q-tab
          name="Box Office Settlements"
          label="Box Office Settlements"
        />
        <q-tab
          name="Showings"
          label="Showings"
        />
      </q-tabs>
      <q-tab-panels
        v-model="tab"
        class="col-12"
      >
        <q-tab-panel
          name="Box Office Settlements"
        >
          <box-office-report-settlement-table
            class="col-12"
            :box-office-report-id="boxOfficeReport.id"
            :column-fn="columns => $_.reject(columns, column => $_.includes(['movie', 'movieTerm', 'site', 'studio'], column?.name || column?.field))"
          />
        </q-tab-panel>
        <q-tab-panel
          name="Showings"
        >
          <showing-table
            class="col-12"
            :box-office-report-id="boxOfficeReport.id"
            initial-period="all"
            :column-fn="columns => $_.reject(columns, column => $_.includes(['movie', 'movieTerm', 'site', 'studio'], column?.name || column?.field))"
          />
        </q-tab-panel>
      </q-tab-panels>
    </div>
  </model-form>
</template>

<script>

import IsoDateInput from 'src/modules/admin/components/IsoDateInput'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import BoxOfficeReportSettlementTable from 'src/modules/admin/settlement/BoxOfficeReportSettlementTable'
import ShowingTable from 'src/modules/admin/showing/ShowingTable'

export default {
  name: 'BoxOfficeReportForm',
  components: {
    IsoDateInput,
    ModelForm,
    BoxOfficeReportSettlementTable,
    ShowingTable
  },
  configuration: ['currentCircuit'],
  models: {
    boxOfficeReport: {
      site: {
        fields: ['id', 'name', 'timeZone']
      },
      movie: {
        studio: 'default'
      },
      movieTerm: {
        movie: 'default',
        showingBadges: 'default'
      }
    }
  },
  data () {
    return {
      tab: 'Box Office Settlements',
      submitSwiftEborLoading: false,
      sendEmailBorLoading: false,
      sendEmailBorSummaryLoading: false,
      forceRecomputeLoading: false
    }
  },
  computed: {
    studio () {
      return this?.boxOfficeReport?.movie?.studio || null
    }
  },
  methods: {
    async forceRecompute () {
      this.forceRecomputeLoading = true
      await this.boxOfficeReport.forceRecompute()
      this.forceRecomputeLoading = false
    },
    async sendEmailBorSummary () {
      this.sendEmailBorSummaryLoading = true
      await this.boxOfficeReport.sendEmailBorSummary()
      this.sendEmailBorSummaryLoading = false
    },
    async sendEmailBor () {
      this.sendEmailBorLoading = true
      await this.boxOfficeReport.sendEmailBor()
      this.sendEmailBorLoading = false
    },
    async submitSwiftEbor () {
      this.submitSwiftEborLoading = true
      await this.boxOfficeReport.submitSwiftEbor()
      this.submitSwiftEborLoading = false
    },
    async sendOrConfirm (callback) {
      if (!_.isNil(this.boxOfficeReport.swiftEborSubmittedAt)) {
        this.$q.dialog({
          title: 'Contact Swift on Resubmission',
          message: 'Swift has previously been submitted for this box office report. Swift will add newly submitted data to their existing submissions, which can result in over-reporting. Are you sure you want to resubmit?',
          ok: {
            label: 'Submit',
            color: 'primary'
          },
          cancel: {
            label: 'Cancel',
            color: 'secondary'
          }
        }).onOk(async () => {
          return callback()
        })
      } else {
        return callback()
      }
    }
  }
}

</script>
